import React from 'react'

import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'

import { validateTemplateForm } from './utils'
import NewAnnotation from './components/NewAnnotation'
import { saveAnnotation } from '../../services/template'
import EditAnnotation from './components/EditAnnotation'
import { Template, AdvancedAnnotation, Period } from '../../types'

export interface TemplateFormProps {
    dashboardId: number
    showActions: boolean
    event: AdvancedAnnotation
    mode: 'temporal' | 'numberic'
    activeDashboardFilters: { [filter: string]: string[] }
    onHide: (
        annotationDataWithExtra: AdvancedAnnotation,
        formData: Record<string, unknown>,
        formInitialState: Record<string, unknown>,
        editable: boolean
    ) => void
}

const TemplateForm: React.FC<TemplateFormProps> = (props: TemplateFormProps) => {
    const handleAnnotationDataWithExtra = (
        annotationData: Record<string, unknown>,
        template: Template,
        period: Period
    ): AdvancedAnnotation => {
        return {
            data: annotationData,
            __color: template?.color,
            templateId: template.id,
            templateName: template.alias,
            begin: period.begin,
            end: period.end || props.event.end,
            dashboardId: props.dashboardId,
            dashboardFilters: props.activeDashboardFilters,
            uid: props.event.uid || undefined,
            __src: props.event.__src || undefined,
            __type: props.event.__type || undefined
        } as AdvancedAnnotation
    }

    const handleAnnotationDataInitialState = (
        annotationData: Record<string, unknown>,
        template: Template
    ): Record<string, unknown> => {
        const initialState = {} as Record<string, unknown>

        Object.keys(annotationData).forEach(key => {
            if (key !== 'templateId') {
                initialState[key] = null
            } else {
                initialState[key] = template?.id
            }
        })

        return initialState
    }

    const submitFormData = async (
        annotationData: Record<string, unknown>,
        template: Template,
        period: Period
    ): Promise<void> => {
        const schema = JSON.parse(template.schema) as FormSchema
        const errors = validateTemplateForm(schema, annotationData)
        const missingRequired = errors.missingRequired

        if (missingRequired.length > 0) {
            const message = `${'One or more required fields are not filled in.'}`
            globalNotification.error(message, 10)
        } else {
            const annotationDataWithExtra = handleAnnotationDataWithExtra(annotationData, template, period)
            const annotationDataInitialState = handleAnnotationDataInitialState(annotationData, template)

            try {
                await saveAnnotation(annotationDataWithExtra)

                props.onHide(
                    annotationDataWithExtra,
                    annotationData,
                    annotationDataInitialState,
                    !!annotationDataWithExtra.uid
                )

                globalNotification.success(i18n('Annotation saved successfully'), 5)
            } catch (error) {
                globalNotification.error(i18n(error.message), 5)
            }
        }
    }

    return (
        <>
            {props.event.templateId ? (
                <EditAnnotation {...props} submitFormData={submitFormData} />
            ) : (
                <NewAnnotation {...props} submitFormData={submitFormData} />
            )}
        </>
    )
}

const TemplateFormRenderer = {
    get title(): string {
        return i18n('Create from template').toUpperCase()
    },
    get editTitle(): string {
        return i18n('Edit custom annotation').toUpperCase()
    },
    get shortTitle(): string {
        return i18n('Annotation template status').toUpperCase()
    },
    component: TemplateForm
}

export default TemplateFormRenderer
